import { SyntheticEvent, useCallback, useState, type ReactNode } from "react";
import { Body, Link, SectionHeader } from "@sprout/typography";
import CompanyLogo from "@components/company_logo";
import { useQueryClient } from "@tanstack/react-query";
import { router } from "@inertiajs/react";
import SearchIcon from "@/sprout/icons/search";
import "@/pages/subscription.scss";
import useTranslation from "@hooks/use_translation";
import { useShowFlash } from "@sprout/flash.tsx";
import TextInputWithTags from "@components/ui/text_input_with_tags.tsx";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@components/ui/dialog.tsx";
import { Trans } from "react-i18next";
import ErrorIcon from "@sprout/icons/error.tsx";
import { EnableAlertsModal } from "@components/job_alerts/enable_alerts_modal.tsx";
import HelpPopover from "@sprout/help_popover.tsx";
import { MultiSelect, type Option } from "@sprout/select";
import axios, { isAxiosError } from "axios";
import { DangerButton, DestructiveButton, PrimaryButton, SecondaryButton } from "@sprout/buttons";

type Method = "PUT" | "POST";

const MAX_KEYWORDS = 8;

export type SubscriptionFormData = {
  departments: Option[];
  selected: Option[];
  pageTitle: string;
  subscriptionPath: string;
  subscriptionsPath: string;
  dashboardPath: string;
  hasSubscriptions: boolean;
  subscriptionId: number;
  method: Method;
  logoUrl?: string;
  companyName: string;
  jobBoardId: number;
  allowDepartmentAlerts: boolean;
  keywords: string[];
  keywordSeeded: boolean;
  departmentSeeded: boolean;
  jobAlertsPaused: boolean;
  emailPreferencesPath?: string;
};

type Props = SubscriptionFormData & {
  onClose?: () => void;
};

const ConfirmationModal = ({
  onConfirm,
  children,
}: {
  onConfirm: () => void;
  children: ReactNode;
}) => {
  return (
    <Dialog>
      <DialogTrigger>{children}</DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="leading-[1.5rem]">
            <div className="flex gap-4">
              <ErrorIcon color="error-red" />
              <Trans i18nKey="jobAlerts:subscription:deleteModal:title" />
            </div>
          </DialogTitle>
        </DialogHeader>
        <DialogDescription>
          <Body>
            <Trans i18nKey="jobAlerts:subscription:deleteModal:description" />
          </Body>
        </DialogDescription>
        <DialogFooter>
          <DialogClose>
            <SecondaryButton>
              <Trans i18nKey="jobAlerts:subscription:deleteModal:cancel" />
            </SecondaryButton>
          </DialogClose>
          <DialogClose>
            <DestructiveButton onClick={onConfirm}>
              <Trans i18nKey="jobAlerts:subscription:deleteModal:confirm" />
            </DestructiveButton>
          </DialogClose>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const DeleteConfirmation = ({
  onConfirm,
  onCancel,
}: {
  onConfirm: () => void;
  onCancel: () => void;
}) => {
  return (
    <>
      <DialogHeader>
        <div className="flex gap-4">
          <ErrorIcon color="error-red" />
          <DialogTitle>
            <h2 className="section-header">
              <Trans i18nKey="jobAlerts:subscription:deleteModal:title" />
            </h2>
          </DialogTitle>
        </div>
      </DialogHeader>
      <Body>
        <Trans i18nKey="jobAlerts:subscription:deleteModal:description" />
      </Body>
      <DialogFooter>
        <SecondaryButton onClick={onCancel}>
          <Trans i18nKey="jobAlerts:subscription:deleteModal:cancel" />
        </SecondaryButton>
        <DestructiveButton onClick={onConfirm}>
          <Trans i18nKey="jobAlerts:subscription:deleteModal:confirm" />
        </DestructiveButton>
      </DialogFooter>
    </>
  );
};

export const SubscriptionForm = ({
  departments,
  method,
  selected,
  pageTitle,
  subscriptionPath,
  subscriptionsPath,
  dashboardPath,
  hasSubscriptions,
  subscriptionId,
  logoUrl,
  companyName,
  jobBoardId,
  keywords,
  keywordSeeded,
  departmentSeeded,
  allowDepartmentAlerts,
  jobAlertsPaused,
  emailPreferencesPath,
  onClose,
}: Props) => {
  const { t } = useTranslation("jobAlerts");
  const [isDirty, setIsDirty] = useState(keywordSeeded || departmentSeeded);
  const [selectedDepartments, setSelectedDepartments] = useState<Option[]>(selected);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formKeywords, setFormKeywords] = useState<string[]>(keywords || []);
  const [keywordError, setKeywordError] = useState<string | null>(null);
  const [departmentError, setDepartmentError] = useState<string | null>(null);
  const [showPausedModal, setShowPausedModal] = useState(method === "POST" && jobAlertsPaused);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const queryClient = useQueryClient();
  const showFlash = useShowFlash();

  const returnPath = hasSubscriptions ? subscriptionsPath : dashboardPath;
  const closeForm = (fallbackPath: string) => (onClose ? onClose() : router.visit(fallbackPath));

  const handleCancel = onClose
    ? (event: SyntheticEvent) => {
        event.preventDefault();
        onClose();
      }
    : undefined;

  const submitSubscriptionSettings = useCallback(async () => {
    if (isSubmitting) {
      return;
    }
    if (selectedDepartments.length > 8) {
      setDepartmentError(t("subscription.department.too_many"));
      return;
    }
    if (formKeywords.length > MAX_KEYWORDS) {
      setKeywordError(t("subscription.keywords.too_many"));
      return;
    }
    setIsSubmitting(true);

    try {
      const payload = {
        id: subscriptionId,
        job_board_id: jobBoardId,
        departments: selectedDepartments.map((department: Option) => department.value),
        keywords: formKeywords,
      };

      await axios({
        method,
        url: subscriptionPath,
        data: payload,
      });

      setIsDirty(false);
      await queryClient.invalidateQueries({ queryKey: ["subscriptions"] });
      showFlash(t(method === "POST" ? "subscription.created" : "subscription.updated"), "success");
      closeForm(method === "POST" ? "/dashboard" : "/subscriptions");
    } catch (e) {
      const errorMessage = (isAxiosError(e) && e.response?.data?.message) ?? t("defaultError");
      setError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  }, [isSubmitting, selectedDepartments, queryClient, router, formKeywords, showFlash]);

  const onSelect = useCallback(
    (values: Option[]) => {
      if (values.length > 8) {
        setDepartmentError(t("subscription.department.too_many"));
        return;
      }

      setDepartmentError(null);
      setSelectedDepartments(values);
      setIsDirty(true);
    },
    [selectedDepartments],
  );

  const handleDelete = useCallback(async () => {
    if (isSubmitting) {
      return;
    }

    try {
      await axios.delete(subscriptionPath);
      await queryClient.invalidateQueries({ queryKey: ["subscriptions"] });
      closeForm("/subscriptions");
    } catch (e) {
      console.error(e);
      setConfirmingDelete(false);
      setError(t("defaultError"));
      setKeywordError(t("defaultError"));
    }
  }, [subscriptionPath, queryClient, t]);

  if (onClose && confirmingDelete) {
    return (
      <DeleteConfirmation onConfirm={handleDelete} onCancel={() => setConfirmingDelete(false)} />
    );
  }

  return (
    <>
      <CompanyLogo companyName={companyName} logoUrl={logoUrl} />
      <div className="flex flex-col gap-[4px]">
        {onClose ? (
          <DialogTitle>
            <h1 className="section-header">{pageTitle}</h1>
          </DialogTitle>
        ) : (
          <SectionHeader as="h1">{pageTitle}</SectionHeader>
        )}
        <Body>{t("subscription.selectJobs")}</Body>
      </div>
      <div className="subscription">
        {allowDepartmentAlerts && departments.length > 0 && (
          <div className="subscription__input">
            <MultiSelect
              values={selectedDepartments}
              label={t("subscription.department.label")}
              onChange={onSelect}
              options={departments}
              clearable
              error={departmentError}
            />
          </div>
        )}

        <TextInputWithTags
          id="keyword"
          className="subscription-keyword"
          values={formKeywords}
          onChange={(nextKeywords) => {
            setFormKeywords(nextKeywords);
            setIsDirty(true);
            setKeywordError(null);
          }}
          max={MAX_KEYWORDS}
          maxMessage={t("subscription.keywords.too_many")}
          addLabel={t("subscription.keywords.add")}
          placeholder={t("subscription.keywords.placeholder")}
          helperText={t("subscription.keywords.subtitle")}
          icon={<SearchIcon />}
          trailing={
            <HelpPopover text={t("subscription.keywords.helperText")} className="h-[40px]" />
          }
          quoted
          short={!onClose}
          error={keywordError}
        />

        {error && (
          <p className="pb-[3px] helper-text helper-text--error" aria-live="polite">
            {error}
          </p>
        )}
        <div className="subscription__buttons">
          <PrimaryButton
            onClick={() => submitSubscriptionSettings()}
            loading={isSubmitting}
            disabled={!isDirty || !(formKeywords.length || selectedDepartments.length)}
          >
            {method === "POST" ? t("subscription.create") : t("subscription.update")}
          </PrimaryButton>
          {method !== "POST" &&
            (onClose ? (
              <DangerButton onClick={() => setConfirmingDelete(true)}>
                {t("subscription.delete")}
              </DangerButton>
            ) : (
              <ConfirmationModal onConfirm={handleDelete}>
                <DangerButton>{t("subscription.delete")}</DangerButton>
              </ConfirmationModal>
            ))}
          <Link href={returnPath} onClick={handleCancel}>
            {"Cancel"}
          </Link>
        </div>
      </div>
      {emailPreferencesPath && (
        <EnableAlertsModal
          open={showPausedModal}
          onOpenChange={setShowPausedModal}
          companyName={companyName}
          emailPreferencesPath={emailPreferencesPath}
          onDismiss={() => setShowPausedModal(false)}
        />
      )}
    </>
  );
};
